// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { AlgorithmType, AlgorithmSubscribeType } from '@shared/commons/cache/Algorithm/Algorithm';
import { DynProperty } from '@shared/commons/DynProperty';
import { DataCache } from '@shared/commons/DataCache';
import { Resources } from '@shared/localizations/Resources';
import HighLowPanelTemplate from '@front/templates/HighLowPanelTemplate.html';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { Control } from '../elements/Control';
import { QuickTableRactive } from '../elements/QuickTable/QuickTableRactive';
import { LinkedSystem } from '../misc/LinkedSystem';
import { HighLowData, HighLowEventType, IsHigh } from '@shared/commons/cache/Algorithm/HighLow/HighLowData';
import { HighLowItem } from '../cache/HighLowItem';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class HighLowPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this.savedExchangesState = null;
        this.exchanges = [];
        this.layoutTable = () => {
            [this.lowsQTR, this.highsQTR].forEach((qtr) => {
                if (!isNullOrUndefined(qtr)) {
                    super.layoutTableResize(qtr);
                }
            });
            const panel = this.getClientPanel();
            if (!isNullOrUndefined(panel)) {
                this.set({
                    showNoDataIcon: panel.offsetWidth > this.MIN_PANEL_WIDTH_FOR_ICON
                });
            }
        };
        this.MIN_PANEL_WIDTH_FOR_ICON = 154; /* #130163 */
        this.UpdateNoData = () => {
            var _a, _b;
            void this.set({
                noDataForLows: ((_a = this.lowsTable) === null || _a === void 0 ? void 0 : _a.rowsArray.length) === 0,
                noDataForHighs: ((_b = this.highsTable) === null || _b === void 0 ? void 0 : _b.rowsArray.length) === 0
            });
        };
        this.IsAllowByFilter = (highLowData) => {
            let allowByExchange = true;
            if (this.exchangeComboBox == null) {
                return false;
            }
            let exchangeItems = this.exchangeComboBox.get('items');
            if (exchangeItems == null) {
                exchangeItems = [];
            }
            const checkedExchanges = exchangeItems.filter((item) => item.checked);
            if (checkedExchanges.length !== exchangeItems.length) {
                allowByExchange =
                    checkedExchanges.filter((exchange) => exchange.value === highLowData.MarketDataExchange).length > 0;
            }
            let allowBySymbol = true;
            const symbolFilter = this.symbolsLookup.get('selectedItem');
            const filterSymbols = symbolFilter.FullName;
            const insArr = symbolFilter.arraySelected || [];
            if (symbolFilter.InstrumentTradableID != null) {
                insArr.push(symbolFilter);
            }
            if (filterSymbols !== 'All' && insArr != null) {
                allowBySymbol = insArr.some((ins) => {
                    return ins.InstrumentTradableID === highLowData.TradableId;
                });
            }
            return allowByExchange && allowBySymbol;
        };
        this.jbInit = () => {
            const highsItems = new HighLowItem(new HighLowData(HighLowEventType.BreakingHigh));
            const lowsItems = new HighLowItem(new HighLowData(HighLowEventType.BreakingLow));
            this.lowsQTR = this.createTable(lowsItems);
            this.highsQTR = this.createTable(highsItems);
            const qtHighs = this.highsQTR.quickTable;
            const qtLows = this.lowsQTR.quickTable;
            if (qtHighs == null || qtLows == null) {
                return;
            }
            this.deserializeDelayedData();
            qtHighs.UpdateSortedColumns();
            qtLows.UpdateSortedColumns();
            this.OnResize.Subscribe(this.layoutTable, this);
            this.on('selectedSymbolsChanged', this.SymbolsLookup_SelectedSymbolChanged);
            this.on('exchangeFilterChanged', this.ExchangeComboBox_ValuesChanged);
            qtHighs.beforeTableContextMenuShowing = this.preparePopup.bind(this);
            qtLows.beforeTableContextMenuShowing = this.preparePopup.bind(this);
            qtHighs.lockManualSorting = qtLows.lockManualSorting = true;
            qtHighs.AddToEnd = qtLows.AddToEnd = false;
            this.localize();
            this.layoutTable();
        };
        this.SymbolsLookup_SelectedSymbolChanged = () => {
            this.ClearByFilter();
        };
        this.ExchangeComboBox_ValuesChanged = () => {
            this.savedExchangesState = this.exchangeComboBox
                .get('items')
                .filter((e) => e.checked)
                .map((e) => e.value);
            this.ClearByFilter();
        };
        this.RepopulateExchanges = () => {
            const oldItems = this.exchangeComboBox.get('items');
            const newItems = this.exchanges.map((str) => {
                return {
                    text: str,
                    value: str,
                    checked: this.savedExchangesState === null ||
                        this.savedExchangesState.includes(str) ||
                        (oldItems != null && this.savedExchangesState.length === oldItems.length) // All exchanges selected (need to include new if received)
                };
            });
            this.exchangeComboBox.set({ items: newItems });
        };
        this.ClearByFilter = () => {
            this.ClearTableByFilter(this.highsTable);
            this.ClearTableByFilter(this.lowsTable);
            this.UpdateNoData();
        };
        this.ClearTableByFilter = (table) => {
            if (table == null) {
                return;
            }
            const itemsToRemove = [];
            table.rowsArray.forEach((row) => {
                if (!this.IsAllowByFilter(row.item.Data)) {
                    itemsToRemove.push(row.item);
                }
            });
            itemsToRemove.forEach((item) => {
                table.RemoveItem(item.ItemId);
            });
            if (itemsToRemove.length > 0) {
                table.OnScroll();
            } // for redraw
        };
        this.createTable = (items) => {
            const qtr = new QuickTableRactive();
            super.addControl(qtr, true);
            const qt = qtr.quickTable;
            qt.allowGroupBy = true;
            qt.InitializeDirect(items);
            qt.UpdateSortedColumns();
            return qtr;
        };
        this.OnClearTableClick = (forHighTable) => {
            const qt = forHighTable ? this.highsTable : this.lowsTable;
            qt === null || qt === void 0 ? void 0 : qt.ClearAll();
            this.UpdateNoData();
        };
        this.Table_SelectionChanged = (onHighsTable, fromMouseClick) => {
            if (fromMouseClick == null) {
                return;
            }
            const selectTable = onHighsTable ? this.highsTable : this.lowsTable;
            const anotherTable = onHighsTable ? this.lowsTable : this.highsTable;
            if (selectTable == null || anotherTable == null) {
                return;
            }
            let selectedRowNum = selectTable.selectedRowIds.length;
            if (selectedRowNum > 1) {
                selectTable.selectedRowIds = [selectTable.selectedRowIds[selectedRowNum - 1]];
                selectedRowNum = 1;
                selectTable.OnScroll(); // for redraw
            }
            if (selectedRowNum === 1) {
                const selectedRow = selectTable.getItemById(selectTable.selectedRowIds[0]);
                const data = selectedRow.item.Data;
                // const selectedIns = DataCache.getInstrument(data.TradableId.toString(), data.RouteId.toString());
                void DataCache.getInstrumentByInstrumentTradableID_NFL(data.TradableId, data.RouteId, null, true).then((selectedIns) => {
                    this.symbolLink_Out(false, selectedIns === null || selectedIns === void 0 ? void 0 : selectedIns.GetInteriorID());
                });
            }
            if (anotherTable != null && anotherTable.selectedRowIds.length > 0) {
                anotherTable.selectedRowIds = [];
                anotherTable.OnScroll(); // for redraw
            }
        };
        this.IsAllowByProductSubscription = (highLowData) => {
            const entitlmentSystemCache = DataCache.EntitlementManager;
            const instrumentId = highLowData.InstrumentId.toString();
            const instrumentGroupId = highLowData.InstrumentGroupId;
            const tradingExchange = highLowData.TradingExchange;
            if (highLowData.IsDelayed) {
                // Don't have RealTime subscription and have Delayed
                return (!entitlmentSystemCache.IsExistSubscription(false, instrumentId, instrumentGroupId, tradingExchange) &&
                    entitlmentSystemCache.IsExistSubscription(true, instrumentId, instrumentGroupId, tradingExchange));
            }
            else {
                return entitlmentSystemCache.IsExistSubscription(false, instrumentId, instrumentGroupId, tradingExchange);
            }
        };
        this.symbolLink_Out = (newSubscriber, instrument) => {
            if (instrument == null) {
                return;
            }
            const color = super.get('symbolLinkValue');
            if (color !== TerceraLinkControlConstants.STATE_NONE) {
                LinkedSystem.setSymbol(color, instrument, newSubscriber);
            }
        };
        this.Subscribe = () => {
            const algorithmCache = DataCache.AlgorithmCache;
            if (algorithmCache != null) {
                const algorithms = algorithmCache.getAlgorithmsByAlgorithmType(AlgorithmType.HighLow);
                algorithms.forEach((algorithm) => {
                    algorithmCache.SubscriptionHandler(AlgorithmSubscribeType.Subscribe, algorithm.AlgorithmId, this);
                });
            }
        };
        this.UnSubscribe = () => {
            const algorithmCache = DataCache.AlgorithmCache;
            if (algorithmCache != null) {
                const algorithms = algorithmCache.getAlgorithmsByAlgorithmType(AlgorithmType.HighLow);
                algorithms.forEach((algorithm) => {
                    algorithmCache.SubscriptionHandler(AlgorithmSubscribeType.Unsubscribe, algorithm.AlgorithmId, this);
                });
            }
        };
        this.PopulateSymbols = () => {
            this.Subscribe();
            // this.algorithmsComboBox?.clearItems();
            // const algorithmCache = DataCache.AlgorithmCache;
            // if (algorithmCache != null) {
            //     const algorithms = [
            //         ...algorithmCache.GetAlgorithmsByAlgorithmType(AlgorithmType.HighLow)
            //     ];
            //     algorithms.sort((x, y) => x.AlgorithmName.localeCompare(y.AlgorithmName));
            //     for (const algorithm of algorithms) {
            //         const comboItem = { text: algorithm.AlgorithmName, value: algorithm.AlgorithmId };
            //         this.algorithmsComboBox.addItem(comboItem);
            //     }
            // }
            // const algoCBItems = this.algorithmsComboBox.get('items');
            // this.algorithmsComboBox.set('visible', algoCBItems.length > 1);
            // if (algoCBItems.length > 0) {
            //     // if (this._subscribedAlgorithmIdProp == null) { this._subscribedAlgorithmIdProp = algoCBItems[0].value; }
            //     // this.algorithmsComboBox.setItembyValue(this._subscribedAlgorithmIdProp);
            // }
        };
        this.createContextMenu = () => {
            var _a, _b;
            const menuItemsHigh = this.createMenuItems(true);
            this.menuTagDictHigh = createTagDictionary(menuItemsHigh);
            (_a = this.highsTable) === null || _a === void 0 ? void 0 : _a.setTableContextMenuItems(menuItemsHigh);
            const menuItemsLow = this.createMenuItems(false);
            this.menuTagDictLow = createTagDictionary(menuItemsLow);
            (_b = this.lowsTable) === null || _b === void 0 ? void 0 : _b.setTableContextMenuItems(menuItemsLow);
        };
        this.createMenuItems = (forHighTable) => {
            const items = [];
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.HighLowPanel.menu.ClearTable',
                event: this.OnClearTableClick.bind(this, forHighTable),
                visible: !Resources.isHidden('panel.HighLowPanel.menu.ClearTable')
            });
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.View',
                svgImage: 'View',
                visible: !Resources.isHidden('panel.HighLowPanel.menu.ShowToolbar'),
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.positions.menu.ShowToolbar',
                        checked: super.get('showToolbar'),
                        canCheck: true,
                        event: this.ShowToolbarStateChange.bind(this)
                    }
                ]
            });
            return items;
        };
        this.ShowToolbarStateChange = (menuItem) => {
            this.ShowToolbarStateUpdate(menuItem.checked);
        };
        this.ShowToolbarStateUpdate = (state) => {
            void this.set('showToolbar', state);
            this.layoutTable();
        };
        this.Properties = () => {
            const props = super.Properties();
            const dp = new DynProperty('SelectedExchangesState', JSON.stringify(this.savedExchangesState), DynProperty.STRING, DynProperty.HIDDEN_GROUP);
            props.push(dp);
            return props;
        };
        this.callBack = (properties) => {
            super.callBack(properties);
            const prop = DynProperty.getPropertyByName(properties, 'SelectedExchangesState');
            if (prop != null) {
                this.savedExchangesState = JSON.parse(prop.value);
            }
        };
        this.Name = 'HighLowPanel';
        this.headerLocaleKey = 'panel.HighLowPanel';
        this.NeedCalculateRowCount = false;
    }
    getType() {
        return PanelNames.HighLowPanel;
    }
    get highsTable() {
        var _a;
        return (_a = this.highsQTR) === null || _a === void 0 ? void 0 : _a.quickTable;
    }
    get lowsTable() {
        var _a;
        return (_a = this.lowsQTR) === null || _a === void 0 ? void 0 : _a.quickTable;
    }
    oninit() {
        super.oninit();
        Control.Ticker.Subscribe(this.TickAsync, this);
    }
    oncomplete() {
        var _a, _b;
        super.oncomplete();
        this.createContextMenu();
        this.repopulate();
        this.localize();
        this.symbolsLookup.IsMultiSelect = true;
        (_a = this.highsTable) === null || _a === void 0 ? void 0 : _a.OnSelectionChanged.Subscribe(this.Table_SelectionChanged.bind(this, true), this);
        (_b = this.lowsTable) === null || _b === void 0 ? void 0 : _b.OnSelectionChanged.Subscribe(this.Table_SelectionChanged.bind(this, false), this);
    }
    dispose() {
        var _a, _b;
        this.UnSubscribe();
        Control.Ticker.UnSubscribe(this.TickAsync, this);
        (_a = this.highsTable) === null || _a === void 0 ? void 0 : _a.OnSelectionChanged.UnSubscribe(this.Table_SelectionChanged, this);
        (_b = this.lowsTable) === null || _b === void 0 ? void 0 : _b.OnSelectionChanged.UnSubscribe(this.Table_SelectionChanged, this);
        super.dispose();
    }
    localize() {
        var _a;
        super.localize();
        const noExchText = Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.No exchanges');
        (_a = this.exchangeComboBox) === null || _a === void 0 ? void 0 : _a.set({
            allItemsSelectedText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.All exchanges'),
            noItemsSelectedText: noExchText,
            noItemsText: noExchText,
            fewItemsSelectedPostfixText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.exchanges')
        });
        if (this.highsTable != null) {
            if (this.highsTable.needRedraw) {
                this.highsTable.localize();
            }
        }
        if (this.lowsTable != null) {
            if (this.lowsTable.needRedraw) {
                this.lowsTable.localize();
            }
        }
        void this.set('noDataText', Resources.getResource('panel.HighLowPanel.noData'));
    }
    repopulate() {
        super.repopulate();
        this.PopulateSymbols();
    }
    TickAsync() {
        this.quickTableTickAsync(this.highsTable);
        this.quickTableTickAsync(this.lowsTable);
        this.forceUpdate = false;
    }
    ProcessAlgorithm(highLowMessage /* DirectAlgorithmHighLowMessage */) {
        const needRepopulateExchangeList = this.ProcessItems(highLowMessage.HighLowItems);
        if (needRepopulateExchangeList || this.savedExchangesState != null) {
            this.RepopulateExchanges();
        }
    }
    ProcessItems(highLowDataItems) {
        const highTable = this.highsTable;
        const lowTable = this.lowsTable;
        if (highTable == null || lowTable == null) {
            return false;
        }
        let needRepopulateExchangeList = false;
        for (const item of highLowDataItems) {
            if (!this.IsAllowByProductSubscription(item)) {
                continue;
            }
            // Update exchange list
            if (item.MarketDataExchange != null && !this.exchanges.includes(item.MarketDataExchange)) {
                this.exchanges.push(item.MarketDataExchange);
                needRepopulateExchangeList = true;
            }
            // Check filter
            if (!this.IsAllowByFilter(item)) {
                continue;
            }
            const tableItem = new HighLowItem(item);
            if (IsHigh(item.EventType)) {
                highTable.AddItem(tableItem);
                highTable.needRedraw = true;
            }
            else {
                lowTable.AddItem(tableItem);
                lowTable.needRedraw = true;
            }
        }
        this.UpdateNoData();
        return needRepopulateExchangeList;
    }
    get symbolsLookup() {
        var _a;
        return (_a = this.Controls) === null || _a === void 0 ? void 0 : _a.SymbolsList;
    }
    get exchangeComboBox() {
        var _a;
        return (_a = this.Controls) === null || _a === void 0 ? void 0 : _a.ExchangeFilter;
    }
    themeChange() {
        if (!isNullOrUndefined(this.highsQTR)) {
            this.highsQTR.themeChange();
        }
        if (!isNullOrUndefined(this.lowsQTR)) {
            this.lowsQTR.themeChange();
        }
    }
    // #region ISerializable
    serialize() {
        const serializedData = super.serialize();
        serializedData.table = {
            highsTable: this.highsTable != null ? this.highsTable.serialize() : {},
            lowsTable: this.lowsTable != null ? this.lowsTable.serialize() : {}
        };
        return serializedData;
    }
    deserialize(serialized) {
        super.deserialize(serialized);
        if (!this.completed) {
            return;
        }
        this.highsTable.deserialize(serialized.table.highsTable);
        this.lowsTable.deserialize(serialized.table.lowsTable);
    }
    deserializeOldScheme(serialized) {
        var _a, _b;
        super.deserializeOldScheme(serialized);
        if (!this.completed) {
            return;
        }
        const highsSettings = (_a = serialized.xmlSettingsTemplate) === null || _a === void 0 ? void 0 : _a.HighsSettings;
        const lowsSettings = (_b = serialized.xmlSettingsTemplate) === null || _b === void 0 ? void 0 : _b.LowsSettings;
        if (highsSettings != null) {
            this.highsTable.deserializeOldScheme(highsSettings);
        }
        if (lowsSettings != null) {
            this.lowsTable.deserializeOldScheme(lowsSettings);
        }
    }
}
ApplicationPanelWithTable.extendWith(HighLowPanel, {
    partials: { bodyPartial: HighLowPanelTemplate },
    data: function () {
        return {
            canLinkByAccount: false,
            isSymbolLinkShow: true,
            showToolbar: true,
            HighsTableCaption: 'High',
            LowsTableCaption: 'Low',
            noDataForLows: true,
            noDataForHighs: true,
            showNoDataIcon: true,
            noDataText: '',
            highsLeft: null,
            tablesHeight: null,
            tablesWidth: null,
            timeRangeFilterVisible: true
        };
    }
});
